import React, { useState, useEffect, useCallback } from 'react';
import { getClassesNeedingSubject, getClassesForSubjectTeacher, assignSubjectTeacher, removeSubjectTeacherAssignment } from '../services/SubjectTeacherService';
import './SubjectTeacherClassBrowser.css';

function SubjectTeacherClassBrowser({ teacherData }) {
  const [availableClasses, setAvailableClasses] = useState([]);
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const specialtySubject = teacherData?.specialtySubject || '';
  const teacherId = teacherData?.uid || teacherData?.id || '';

  const loadClasses = useCallback(async () => {
    if (!specialtySubject) return;
    setLoading(true);

    try {
      // Load classes needing this subject
      const availableResult = await getClassesNeedingSubject(specialtySubject);
      if (availableResult.success) {
        // Load assigned classes
        const assignedResult = await getClassesForSubjectTeacher(teacherId, specialtySubject);
        if (assignedResult.success) {
          const assignedIds = new Set(assignedResult.classes.map(c => c.id));
          setAvailableClasses(availableResult.classes.filter(c => !assignedIds.has(c.id)));
          setAssignedClasses(assignedResult.classes);
        } else {
          setAvailableClasses(availableResult.classes);
        }
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [specialtySubject, teacherId]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const handleAssign = async (schoolClass) => {
    try {
      const result = await assignSubjectTeacher(teacherId, schoolClass.id, specialtySubject);
      if (result.success) {
        setSuccessMessage(`Successfully assigned to ${schoolClass.name}`);
        setShowSuccessAlert(true);
        loadClasses();
      } else {
        alert('Failed to assign to class');
      }
    } catch (error) {
      console.error('Error assigning to class:', error);
      alert('Failed to assign to class');
    }
  };

  const handleUnassign = async (schoolClass) => {
    try {
      const result = await removeSubjectTeacherAssignment(schoolClass.id, specialtySubject);
      if (result.success) {
        setSuccessMessage(`Successfully unassigned from ${schoolClass.name}`);
        setShowSuccessAlert(true);
        loadClasses();
      } else {
        alert('Failed to unassign from class');
      }
    } catch (error) {
      console.error('Error unassigning from class:', error);
      alert('Failed to unassign from class');
    }
  };

  const handleBulkAssign = async () => {
    const classesToAssign = availableClasses.filter(c => selectedClassIds.has(c.id));
    let successCount = 0;

    for (const schoolClass of classesToAssign) {
      try {
        await assignSubjectTeacher(teacherId, schoolClass.id, specialtySubject);
        successCount++;
      } catch (error) {
        console.error('Error assigning to class:', schoolClass.name, error);
      }
    }

    setSuccessMessage(`Assigned to ${successCount} class${successCount > 1 ? 'es' : ''} successfully`);
    setShowSuccessAlert(true);
    setSelectedClassIds(new Set());
    setIsMultiSelectMode(false);
    loadClasses();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading classes...</p>
      </div>
    );
  }

  return (
    <div className="subject-teacher-browser">
      <div className="view-header">
        <h2>Subject Teacher Portal</h2>
        <span className="specialty-badge">Specialty: {specialtySubject}</span>
      </div>

      <div className="sections-container">
        {/* Available Classes Section */}
        <div className="section">
          <div className="section-header">
            <h3>Classes Needing {specialtySubject}</h3>
            <button
              className={`multi-select-toggle ${isMultiSelectMode ? 'active' : ''}`}
              onClick={() => {
                setIsMultiSelectMode(!isMultiSelectMode);
                setSelectedClassIds(new Set());
              }}
            >
              {isMultiSelectMode ? 'Done' : 'Select Multiple'}
            </button>
          </div>
          {availableClasses.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <p>No classes currently need {specialtySubject}</p>
            </div>
          ) : (
            <>
              <div className="classes-list">
                {availableClasses.map(schoolClass => (
                  isMultiSelectMode ? (
                    <MultiSelectClassCard
                      key={schoolClass.id}
                      schoolClass={schoolClass}
                      isSelected={selectedClassIds.has(schoolClass.id)}
                      onToggle={() => {
                        const newSet = new Set(selectedClassIds);
                        if (newSet.has(schoolClass.id)) {
                          newSet.delete(schoolClass.id);
                        } else {
                          newSet.add(schoolClass.id);
                        }
                        setSelectedClassIds(newSet);
                      }}
                    />
                  ) : (
                    <ClassAssignmentCard
                      key={schoolClass.id}
                      schoolClass={schoolClass}
                      isAssigned={false}
                      specialtySubject={specialtySubject}
                      onAssign={() => handleAssign(schoolClass)}
                    />
                  )
                ))}
              </div>
              {isMultiSelectMode && selectedClassIds.size > 0 && (
                <button className="bulk-assign-button" onClick={handleBulkAssign}>
                  Assign to {selectedClassIds.size} Class{selectedClassIds.size > 1 ? 'es' : ''}
                </button>
              )}
            </>
          )}
        </div>

        {/* Assigned Classes Section */}
        <div className="section">
          <h3>My Assigned Classes</h3>
          {assignedClasses.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>You haven't been assigned to any classes yet</p>
            </div>
          ) : (
            <div className="classes-list">
              {assignedClasses.map(schoolClass => (
                <ClassAssignmentCard
                  key={schoolClass.id}
                  schoolClass={schoolClass}
                  isAssigned={true}
                  specialtySubject={specialtySubject}
                  onUnassign={() => handleUnassign(schoolClass)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showSuccessAlert && (
        <div className="success-alert">
          <p>{successMessage}</p>
          <button onClick={() => setShowSuccessAlert(false)}>Close</button>
        </div>
      )}
    </div>
  );
}

function ClassAssignmentCard({ schoolClass, isAssigned, specialtySubject, onAssign, onUnassign }) {
  return (
    <div className="class-assignment-card">
      <div className="card-header">
        <div className="class-info">
          <h4>{schoolClass.name}</h4>
          <p>Grade: {schoolClass.grade}</p>
        </div>
        {isAssigned ? (
          <span className="status-badge assigned">✓ Assigned</span>
        ) : (
          <span className="status-badge available">+ Available</span>
        )}
      </div>

      {schoolClass.teacherId && (
        <p className="teacher-info">Home Teacher ID: {schoolClass.teacherId.substring(0, 8)}</p>
      )}

      <button
        className={`action-button ${isAssigned ? 'unassign' : 'assign'}`}
        onClick={isAssigned ? onUnassign : onAssign}
      >
        {isAssigned ? `Unassign from ${specialtySubject}` : `Assign to ${specialtySubject}`}
      </button>
    </div>
  );
}

function MultiSelectClassCard({ schoolClass, isSelected, onToggle }) {
  return (
    <div
      className={`multi-select-card ${isSelected ? 'selected' : ''}`}
      onClick={onToggle}
    >
      <div className="card-header">
        <div className="class-info">
          <h4>{schoolClass.name}</h4>
          <p>Grade: {schoolClass.grade}</p>
        </div>
        <span className={`checkbox ${isSelected ? 'checked' : ''}`}>
          {isSelected && '✓'}
        </span>
      </div>
    </div>
  );
}

export default SubjectTeacherClassBrowser;
